import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faArrowRightArrowLeft,
  faCheck,
  faSliders,
  faCircleNotch,
  faArrowRight,
  faArrowLeft,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import { ViewUploadZone } from "../design-create/components/view-upload-zone.component";
import { PPCMCalibrationPanel } from "../design-create/components/ppcm-calibration-panel.component";

// Message Types
interface EditorConfig {
  theme?: {
    primaryColor?: string; // Primary button color
    backgroundColor?: string; // Main background color
    accentColor?: string; // Accent for progress, success states
  };
  layout?: {
    padding?: string; // Container padding (e.g., "1rem", "24px")
    cardBorderRadius?: string; // Card corners (e.g., "12px", "0")
    cardGap?: string; // Space between cards (e.g., "1.5rem")
  };
}

interface InitMessage {
  type: "INIT_STYLE_UPLOAD";
  apiKey: string;
  config?: EditorConfig;
  language?: "da" | "en";
}

type Language = "da" | "en";

const translations = {
  en: {
    uploadCustomStyle: "Upload Custom Style",
    step: "Step",
    of: "of",
    styleDetails: "Style Details",
    calibrateSize: "Calibrate Size",
    reviewSubmit: "Review & Submit",
    basicInformation: "Basic Information",
    basicInfoDesc:
      "Enter the brand, model, and category for your custom style.",
    brand: "Brand",
    model: "Model",
    category: "Category",
    brandPlaceholder: "e.g., Nike, Adidas",
    modelPlaceholder: "e.g., 123456-78",
    selectCategory: "Select category...",
    loading: "Loading...",
    productImages: "Product Images",
    productImagesDesc: "Upload images for each view. Front view is required.",
    sizeCalibrated: "Size calibrated",
    recalibrate: "Recalibrate",
    mirrorLeftToRight: "Mirror Left image to create Right view",
    mirrorRightToLeft: "Mirror Right image to create Left view",
    continueToCalibration: "Continue to Calibration",
    reviewYourStyle: "Review Your Style",
    reviewDesc: "Confirm the details before creating your custom style.",
    uploadedViews: "Uploaded Views",
    mirrored: "Mirrored",
    notUploaded: "Not uploaded",
    backToEdit: "Back to Edit",
    createStyle: "Create Style",
    creatingStyle: "Creating Style...",
    initializingStyleUpload: "Initializing Style Upload",
    waitingForConfig: "Waiting for configuration...",
    front: "Front",
    back: "Back",
    left: "Left",
    right: "Right",
    // ViewUploadZone translations
    remove: "Remove",
    disabled: "Disabled",
    dragDropOrClick: "Drag & drop or click to upload",
    dropImageHere: "Drop image here",
    fileSizeError: "File size must be less than {size} MB",
    // PPCMCalibrationPanel translations
    calibrationTitle: "Calibrate Logo Size",
    calibrationDesc: "This helps us scale your logos correctly when you place them on the design.",
    calibrationInstruction: "Adjust the slider until the blue box looks like 10 cm would look on your actual garment.",
    calibrationDragHint: "Drag the box to move it around the image.",
    smallerLogo: "Smaller logo",
    largerLogo: "Larger logo",
    confirmContinue: "Confirm & Continue",
  },
  da: {
    uploadCustomStyle: "Upload Tøjartikel",
    step: "Trin",
    of: "af",
    styleDetails: "Detaljer",
    calibrateSize: "Kalibrer Størrelse",
    reviewSubmit: "Gennemgå & Indsend",
    basicInformation: "Grundlæggende Information",
    basicInfoDesc: "Indtast tøjmærke, model og kategori for tøjartiklen.",
    brand: "Tøjmærke",
    model: "Model",
    category: "Kategori",
    brandPlaceholder: "f.eks. Mascot, Adidas",
    modelPlaceholder: "f.eks. 123456-78",
    selectCategory: "Vælg kategori...",
    loading: "Indlæser...",
    productImages: "Produktbilleder",
    productImagesDesc: "Upload billeder for hver visning. Front er påkrævet.",
    sizeCalibrated: "Størrelse kalibreret",
    recalibrate: "Genkalibrer",
    mirrorLeftToRight: "Spejl venstre billede for at oprette højre visning",
    mirrorRightToLeft: "Spejl højre billede for at oprette venstre visning",
    continueToCalibration: "Fortsæt til Kalibrering",
    reviewYourStyle: "Gennemgå Din Tøjartikel",
    reviewDesc: "Bekræft detaljerne før du opretter din tøjartikel.",
    uploadedViews: "Uploadede Visninger",
    mirrored: "Spejlet",
    notUploaded: "Ikke uploadet",
    backToEdit: "Tilbage til Redigering",
    createStyle: "Opret Tøjartikel",
    creatingStyle: "Opretter...",
    initializingStyleUpload: "Initialiserer",
    waitingForConfig: "Venter på konfiguration...",
    front: "Front",
    back: "Bag",
    left: "Venstre",
    right: "Højre",
    // ViewUploadZone translations
    remove: "Fjern",
    disabled: "Deaktiveret",
    dragDropOrClick: "Træk og slip eller klik for at uploade",
    dropImageHere: "Slip billedet her",
    fileSizeError: "Filstørrelsen skal være mindre end {size} MB",
    // PPCMCalibrationPanel translations
    calibrationTitle: "Kalibrer Logostørrelse",
    calibrationDesc: "Dette hjælper os med at skalere dine logoer korrekt, når du placerer dem på designet.",
    calibrationInstruction: "Juster skyderen indtil den blå boks ser ud som 10 cm ville se ud på din faktiske beklædningsgenstand.",
    calibrationDragHint: "Træk i boksen for at flytte den rundt på billedet.",
    smallerLogo: "Mindre logo",
    largerLogo: "Større logo",
    confirmContinue: "Bekræft & Fortsæt",
  },
};

interface StyleCategory {
  key: string;
  name: string;
  defaultPpcm: number;
}

interface StyleViewDto {
  viewId: number;
  view: string;
}

interface CreateStyleResponse {
  styleId: number;
  name: string;
  model: string;
  manufacturerId: number;
  brandName: string;
  views: StyleViewDto[];
}

const EXTERNAL_API_URL = process.env.REACT_APP_EXTERNAL_API_URL;

type Step = "info" | "calibration" | "review";

export const ExternalStyleUploadScreen: React.FC = () => {
  // Init state
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [config, setConfig] = useState<EditorConfig | undefined>(undefined);
  const [language, setLanguage] = useState<Language>("en");
  const [isInitialized, setIsInitialized] = useState(false);

  // Translation helper
  const t = translations[language];

  // Form state
  const [brandName, setBrandName] = useState("");
  const [modelName, setModelName] = useState("");
  const [categoryKey, setCategoryKey] = useState("");
  const [categories, setCategories] = useState<StyleCategory[]>([]);
  const [frontImage, setFrontImage] = useState<File | null>(null);
  const [backImage, setBackImage] = useState<File | null>(null);
  const [leftImage, setLeftImage] = useState<File | null>(null);
  const [rightImage, setRightImage] = useState<File | null>(null);
  const [mirrorLeftToRight, setMirrorLeftToRight] = useState(false);
  const [mirrorRightToLeft, setMirrorRightToLeft] = useState(false);
  const [calibratedPpcm, setCalibratedPpcm] = useState<number | null>(null);

  // UI state
  const [currentStep, setCurrentStep] = useState<Step>("info");
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [leftPreviewUrl, setLeftPreviewUrl] = useState<string | null>(null);
  const [rightPreviewUrl, setRightPreviewUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Listen for init message
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const data = event.data as InitMessage;
      if (data && data.type === "INIT_STYLE_UPLOAD") {
        console.log("Received init message", data);
        setApiKey(data.apiKey);
        setConfig(data.config);
        // Set language (default to English if not provided or invalid)
        if (data.language === "da" || data.language === "en") {
          setLanguage(data.language);
        } else {
          setLanguage("en");
        }
        setIsInitialized(true);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Fetch categories when API key is available
  useEffect(() => {
    if (!apiKey) return;

    setIsLoadingCategories(true);
    fetch(`${EXTERNAL_API_URL}/styles/categories`, {
      headers: {
        "X-Api-Key": apiKey,
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((cats: StyleCategory[]) => {
        setCategories(cats);
        if (cats.length > 0) {
          setCategoryKey(cats[0].key);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch categories:", err);
        setError("Failed to load categories. Please refresh and try again.");
      })
      .finally(() => setIsLoadingCategories(false));
  }, [apiKey]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setCategoryDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Apply theme colors
  useEffect(() => {
    if (config?.theme?.primaryColor) {
      document.documentElement.style.setProperty(
        "--external-primary",
        config.theme.primaryColor,
      );
    }
    if (config?.theme?.backgroundColor) {
      document.documentElement.style.setProperty(
        "--external-bg",
        config.theme.backgroundColor,
      );
    }
  }, [config]);

  const selectedCategory = categories.find((c) => c.key === categoryKey);
  const formIsComplete =
    brandName.trim() !== "" &&
    modelName.trim() !== "" &&
    categoryKey !== "" &&
    frontImage !== null;
  const isReadyToSubmit = formIsComplete && calibratedPpcm !== null;

  function handleLeftImageSelect(file: File | null) {
    if (leftPreviewUrl) URL.revokeObjectURL(leftPreviewUrl);
    setLeftImage(file);
    if (file) {
      setLeftPreviewUrl(URL.createObjectURL(file));
    } else {
      setLeftPreviewUrl(null);
      setMirrorLeftToRight(false);
    }
  }

  function handleRightImageSelect(file: File | null) {
    if (rightPreviewUrl) URL.revokeObjectURL(rightPreviewUrl);
    setRightImage(file);
    if (file) {
      setRightPreviewUrl(URL.createObjectURL(file));
    } else {
      setRightPreviewUrl(null);
      setMirrorRightToLeft(false);
    }
  }

  function handleFrontImageSelect(file: File | null) {
    setFrontImage(file);
    if (!file || file !== frontImage) {
      setCalibratedPpcm(null);
    }
  }

  function handleCalibrationComplete(ppcm: number) {
    setCalibratedPpcm(ppcm);
    setCurrentStep("review");
  }

  function handleCalibrationBack() {
    setCurrentStep("info");
  }

  async function handleSubmit() {
    if (!isReadyToSubmit || !frontImage || !calibratedPpcm || !apiKey) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("brandName", brandName.trim());
      formData.append("modelName", modelName.trim());
      formData.append("categoryKey", categoryKey);
      formData.append("ppcm", calibratedPpcm.toString());
      formData.append("frontImage", frontImage);

      if (backImage) {
        formData.append("backImage", backImage);
      }
      if (leftImage) {
        formData.append("leftImage", leftImage);
      }
      if (rightImage) {
        formData.append("rightImage", rightImage);
      }

      // Handle mirroring
      if (mirrorLeftToRight && leftImage && !rightImage) {
        formData.append("mirrorLeftToRight", "true");
      }
      if (mirrorRightToLeft && rightImage && !leftImage) {
        formData.append("mirrorRightToLeft", "true");
      }

      const response = await fetch(`${EXTERNAL_API_URL}/styles`, {
        method: "POST",
        headers: {
          "X-Api-Key": apiKey,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create style");
      }

      const result: CreateStyleResponse = await response.json();

      // Send success message to parent
      window.parent.postMessage(
        {
          type: "STYLE_CREATED",
          styleId: result.styleId,
          style: result,
        },
        "*",
      );
    } catch (err: any) {
      console.error("Failed to create style:", err);
      setError(err.message || "Failed to create style. Please try again.");

      // Send error message to parent
      window.parent.postMessage(
        {
          type: "STYLE_CREATE_ERROR",
          error: err.message || "Unknown error",
        },
        "*",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  // Waiting for initialization
  if (!isInitialized) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{
          background:
            "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
        }}
      >
        <div className="text-center">
          <div className="relative mb-8">
            <div className="w-20 h-20 border-4 border-slate-600 border-t-emerald-400 rounded-full animate-spin mx-auto" />
            <div className="absolute inset-0 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faUpload}
                className="text-emerald-400 text-2xl"
              />
            </div>
          </div>
          <h2 className="text-xl font-semibold text-slate-200 tracking-wide">
            {t.initializingStyleUpload}
          </h2>
          <p className="text-sm text-slate-400 mt-2">{t.waitingForConfig}</p>
        </div>
      </div>
    );
  }

  // Step indicators
  const steps: { key: Step; label: string; number: number }[] = [
    { key: "info", label: t.styleDetails, number: 1 },
    { key: "calibration", label: t.calibrateSize, number: 2 },
    { key: "review", label: t.reviewSubmit, number: 3 },
  ];

  const currentStepIndex = steps.findIndex((s) => s.key === currentStep);

  // Config-derived styles
  const accentColor = config?.theme?.accentColor || "#10b981";
  const primaryColor = config?.theme?.primaryColor || "#1e293b";
  const backgroundColor = config?.theme?.backgroundColor || "#f8fafc";
  const containerPadding = config?.layout?.padding || "1.5rem";
  const cardBorderRadius = config?.layout?.cardBorderRadius || "0.75rem";
  const cardGap = config?.layout?.cardGap || "2rem";

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundColor,
        padding: containerPadding,
      }}
    >
      <div className="max-w-4xl mx-auto flex flex-col" style={{ gap: cardGap }}>
        {/* Progress card (floating) */}
        <div
          className="bg-white shadow-sm overflow-hidden"
          style={{ borderRadius: cardBorderRadius }}
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-lg font-bold text-slate-800 tracking-tight">
                {t.uploadCustomStyle}
              </h1>
              <span className="text-xs text-slate-400 uppercase tracking-widest">
                {t.step} {currentStepIndex + 1} {t.of} {steps.length}
              </span>
            </div>

            {/* Progress bar */}
            <div className="flex items-center gap-2">
              {steps.map((step, idx) => (
                <React.Fragment key={step.key}>
                  <div
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all`}
                    style={{
                      backgroundColor:
                        idx < currentStepIndex
                          ? `${accentColor}20`
                          : idx === currentStepIndex
                            ? primaryColor
                            : "#f1f5f9",
                      color:
                        idx < currentStepIndex
                          ? accentColor
                          : idx === currentStepIndex
                            ? "white"
                            : "#94a3b8",
                    }}
                  >
                    {idx < currentStepIndex ? (
                      <FontAwesomeIcon icon={faCheck} className="text-xs" />
                    ) : (
                      <span className="w-4 h-4 flex items-center justify-center rounded-full bg-current/10 text-current text-[10px]">
                        {step.number}
                      </span>
                    )}
                    <span className="hidden sm:inline">{step.label}</span>
                  </div>
                  {idx < steps.length - 1 && (
                    <div
                      className="flex-1 h-0.5 rounded"
                      style={{
                        backgroundColor:
                          idx < currentStepIndex ? accentColor : "#e2e8f0",
                      }}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* Main content */}
        {error && (
          <div
            className="p-4 bg-red-50 border border-red-200"
            style={{ borderRadius: cardBorderRadius }}
          >
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Step 1: Info */}
        {currentStep === "info" && (
          <div
            className="flex flex-col animate-fadeIn"
            style={{ gap: cardGap }}
          >
            {/* Form card */}
            <div
              className={`bg-white shadow-sm ${categoryDropdownOpen ? "overflow-visible z-10" : "overflow-hidden"}`}
              style={{ borderRadius: cardBorderRadius }}
            >
              <div className="p-6 border-b border-slate-100">
                <h2 className="text-base font-semibold text-slate-800">
                  {t.basicInformation}
                </h2>
                <p className="text-sm text-slate-500 mt-1">{t.basicInfoDesc}</p>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Brand */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t.brand} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={brandName}
                      onChange={(e) => setBrandName(e.target.value)}
                      placeholder={t.brandPlaceholder}
                      maxLength={50}
                      className="w-full h-10 px-3 text-sm bg-slate-50 border border-slate-200 rounded-lg text-slate-800 placeholder-slate-400 focus:outline-none focus:border-slate-400 focus:bg-white transition-all"
                    />
                  </div>

                  {/* Model */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t.model} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={modelName}
                      onChange={(e) => setModelName(e.target.value)}
                      placeholder={t.modelPlaceholder}
                      maxLength={50}
                      className="w-full h-10 px-3 text-sm bg-slate-50 border border-slate-200 rounded-lg text-slate-800 placeholder-slate-400 focus:outline-none focus:border-slate-400 focus:bg-white transition-all"
                    />
                  </div>

                  {/* Category */}
                  <div
                    ref={dropdownRef}
                    className={`relative ${categoryDropdownOpen ? "z-50" : ""}`}
                  >
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t.category} <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={() =>
                        setCategoryDropdownOpen(!categoryDropdownOpen)
                      }
                      disabled={isLoadingCategories}
                      className="w-full h-10 px-3 text-sm bg-slate-50 border border-slate-200 rounded-lg text-slate-800 focus:outline-none focus:border-slate-400 cursor-pointer flex items-center justify-between transition-all hover:bg-white disabled:opacity-50"
                    >
                      <span>
                        {isLoadingCategories
                          ? t.loading
                          : selectedCategory?.name || t.selectCategory}
                      </span>
                      <FontAwesomeIcon
                        icon={
                          isLoadingCategories ? faCircleNotch : faChevronDown
                        }
                        className={`text-slate-400 text-xs ${
                          isLoadingCategories ? "animate-spin" : ""
                        } ${categoryDropdownOpen ? "rotate-180" : ""} transition-transform`}
                      />
                    </button>
                    {categoryDropdownOpen && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                        {categories.map((cat) => (
                          <div
                            key={cat.key}
                            onClick={() => {
                              setCategoryKey(cat.key);
                              setCategoryDropdownOpen(false);
                            }}
                            className={`px-3 py-2.5 text-sm cursor-pointer transition-colors ${
                              cat.key === categoryKey
                                ? "bg-slate-100 text-slate-900 font-medium"
                                : "text-slate-700 hover:bg-slate-50"
                            }`}
                          >
                            {cat.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Image uploads card */}
            <div
              className="bg-white shadow-sm overflow-hidden"
              style={{ borderRadius: cardBorderRadius }}
            >
              <div className="p-6 border-b border-slate-100">
                <h2 className="text-base font-semibold text-slate-800">
                  {t.productImages}
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  {t.productImagesDesc}
                </p>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="flex flex-col">
                    <ViewUploadZone
                      viewName={t.front}
                      required={true}
                      file={frontImage}
                      onFileSelect={handleFrontImageSelect}
                      translations={{
                        remove: t.remove,
                        mirrored: t.mirrored,
                        disabled: t.disabled,
                        dragDropOrClick: t.dragDropOrClick,
                        dropImageHere: t.dropImageHere,
                        fileSizeError: t.fileSizeError,
                      }}
                    />
                    {frontImage && calibratedPpcm !== null && (
                      <div className="mt-3 flex items-center justify-between">
                        <span
                          className="text-xs flex items-center gap-1.5"
                          style={{ color: accentColor }}
                        >
                          <FontAwesomeIcon icon={faCheck} size="xs" />
                          {t.sizeCalibrated}
                        </span>
                        <button
                          type="button"
                          onClick={() => setCurrentStep("calibration")}
                          className="text-xs text-slate-600 hover:text-slate-800 flex items-center gap-1.5 underline"
                        >
                          <FontAwesomeIcon icon={faSliders} size="xs" />
                          {t.recalibrate}
                        </button>
                      </div>
                    )}
                  </div>

                  <ViewUploadZone
                    viewName={t.back}
                    required={false}
                    file={backImage}
                    onFileSelect={setBackImage}
                    translations={{
                      remove: t.remove,
                      mirrored: t.mirrored,
                      disabled: t.disabled,
                      dragDropOrClick: t.dragDropOrClick,
                      dropImageHere: t.dropImageHere,
                      fileSizeError: t.fileSizeError,
                    }}
                  />

                  <ViewUploadZone
                    viewName={t.left}
                    required={false}
                    file={leftImage}
                    onFileSelect={handleLeftImageSelect}
                    disabled={mirrorRightToLeft}
                    mirroredPreviewUrl={
                      mirrorRightToLeft ? rightPreviewUrl : null
                    }
                    translations={{
                      remove: t.remove,
                      mirrored: t.mirrored,
                      disabled: t.disabled,
                      dragDropOrClick: t.dragDropOrClick,
                      dropImageHere: t.dropImageHere,
                      fileSizeError: t.fileSizeError,
                    }}
                  />

                  <ViewUploadZone
                    viewName={t.right}
                    required={false}
                    file={rightImage}
                    onFileSelect={handleRightImageSelect}
                    disabled={mirrorLeftToRight}
                    mirroredPreviewUrl={
                      mirrorLeftToRight ? leftPreviewUrl : null
                    }
                    translations={{
                      remove: t.remove,
                      mirrored: t.mirrored,
                      disabled: t.disabled,
                      dragDropOrClick: t.dragDropOrClick,
                      dropImageHere: t.dropImageHere,
                      fileSizeError: t.fileSizeError,
                    }}
                  />
                </div>

                {/* Mirroring options */}
                {leftImage && !rightImage && !mirrorRightToLeft && (
                  <label className="flex items-center gap-3 mt-6 cursor-pointer text-sm text-slate-700 hover:text-slate-900 transition-colors">
                    <input
                      type="checkbox"
                      checked={mirrorLeftToRight}
                      onChange={(e) => setMirrorLeftToRight(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300 text-slate-800 focus:ring-slate-500"
                    />
                    <FontAwesomeIcon
                      icon={faArrowRightArrowLeft}
                      className="text-slate-400"
                    />
                    <span>{t.mirrorLeftToRight}</span>
                  </label>
                )}
                {rightImage && !leftImage && !mirrorLeftToRight && (
                  <label className="flex items-center gap-3 mt-6 cursor-pointer text-sm text-slate-700 hover:text-slate-900 transition-colors">
                    <input
                      type="checkbox"
                      checked={mirrorRightToLeft}
                      onChange={(e) => setMirrorRightToLeft(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300 text-slate-800 focus:ring-slate-500"
                    />
                    <FontAwesomeIcon
                      icon={faArrowRightArrowLeft}
                      className="text-slate-400"
                    />
                    <span>{t.mirrorRightToLeft}</span>
                  </label>
                )}
              </div>
            </div>

            {/* Continue button card */}
            <div
              className="bg-white shadow-sm overflow-hidden"
              style={{ borderRadius: cardBorderRadius }}
            >
              <div className="p-6 flex justify-end">
                <button
                  onClick={() => setCurrentStep("calibration")}
                  disabled={!formIsComplete}
                  className="px-6 py-2.5 text-sm font-medium text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: formIsComplete ? primaryColor : "#94a3b8",
                  }}
                >
                  {t.continueToCalibration}
                  <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Calibration */}
        {currentStep === "calibration" && frontImage && (
          <div
            className="bg-white shadow-sm overflow-hidden animate-fadeIn"
            style={{ borderRadius: cardBorderRadius }}
          >
            <PPCMCalibrationPanel
              imageFile={frontImage}
              onCalibrated={handleCalibrationComplete}
              onBack={handleCalibrationBack}
              initialPpcm={
                calibratedPpcm || selectedCategory?.defaultPpcm || 22
              }
              translations={{
                title: t.calibrationTitle,
                description: t.calibrationDesc,
                instruction: t.calibrationInstruction,
                dragHint: t.calibrationDragHint,
                smallerLogo: t.smallerLogo,
                largerLogo: t.largerLogo,
                back: t.backToEdit,
                confirmContinue: t.confirmContinue,
              }}
            />
          </div>
        )}

        {/* Step 3: Review */}
        {currentStep === "review" && (
          <div
            className="flex flex-col animate-fadeIn"
            style={{ gap: cardGap }}
          >
            {/* Summary card */}
            <div
              className="bg-white shadow-sm overflow-hidden"
              style={{ borderRadius: cardBorderRadius }}
            >
              <div className="p-6 border-b border-slate-100">
                <h2 className="text-base font-semibold text-slate-800">
                  {t.reviewYourStyle}
                </h2>
                <p className="text-sm text-slate-500 mt-1">{t.reviewDesc}</p>
              </div>

              <div className="p-6">
                {/* Details grid */}
                <div className="grid grid-cols-3 gap-6 mb-8">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <span className="text-xs text-slate-500 uppercase tracking-wide">
                      {t.brand}
                    </span>
                    <p className="text-sm font-semibold text-slate-800 mt-1">
                      {brandName}
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <span className="text-xs text-slate-500 uppercase tracking-wide">
                      {t.model}
                    </span>
                    <p className="text-sm font-semibold text-slate-800 mt-1">
                      {modelName}
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <span className="text-xs text-slate-500 uppercase tracking-wide">
                      {t.category}
                    </span>
                    <p className="text-sm font-semibold text-slate-800 mt-1">
                      {selectedCategory?.name || categoryKey}
                    </p>
                  </div>
                </div>

                {/* Image previews */}
                <div>
                  <h3 className="text-sm font-medium text-slate-700 mb-4">
                    {t.uploadedViews}
                  </h3>
                  <div className="grid grid-cols-4 gap-4">
                    {[
                      { name: t.front, file: frontImage },
                      { name: t.back, file: backImage },
                      {
                        name: t.left,
                        file: leftImage,
                        mirrored: mirrorRightToLeft,
                      },
                      {
                        name: t.right,
                        file: rightImage,
                        mirrored: mirrorLeftToRight,
                      },
                    ].map((view) => (
                      <div
                        key={view.name}
                        className="aspect-square rounded-lg border-2 overflow-hidden"
                        style={{
                          borderColor:
                            view.file || view.mirrored
                              ? accentColor
                              : "#e2e8f0",
                          backgroundColor:
                            view.file || view.mirrored
                              ? `${accentColor}10`
                              : "#f8fafc",
                        }}
                      >
                        {view.file ? (
                          <img
                            src={URL.createObjectURL(view.file)}
                            alt={view.name}
                            className="w-full h-full object-contain"
                          />
                        ) : view.mirrored ? (
                          <div className="w-full h-full flex items-center justify-center text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <FontAwesomeIcon icon={faArrowRightArrowLeft} />
                              {t.mirrored}
                            </span>
                          </div>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-xs text-slate-400">
                            {t.notUploaded}
                          </div>
                        )}
                        <div className="text-center py-1 text-xs font-medium text-slate-600 bg-white/80">
                          {view.name}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Action buttons card */}
            <div
              className="bg-white shadow-sm overflow-hidden"
              style={{ borderRadius: cardBorderRadius }}
            >
              <div className="p-6 flex justify-between">
                <button
                  onClick={() => setCurrentStep("info")}
                  disabled={isSubmitting}
                  className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-all disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                  {t.backToEdit}
                </button>

                <button
                  onClick={handleSubmit}
                  disabled={!isReadyToSubmit || isSubmitting}
                  className="px-8 py-2.5 text-sm font-medium text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor:
                      isReadyToSubmit && !isSubmitting
                        ? primaryColor
                        : "#94a3b8",
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <FontAwesomeIcon
                        icon={faCircleNotch}
                        className="mr-2 animate-spin"
                      />
                      {t.creatingStyle}
                    </>
                  ) : (
                    <>
                      {t.createStyle}
                      <FontAwesomeIcon icon={faCheck} className="ml-2" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default ExternalStyleUploadScreen;
